import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ELanguage } from '@app/app.interface';
import { INews } from '@core/interfaces/news.interface';
import { NewsService } from '@core/services/news.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DateTimeRangeFieldsComponent } from '@shared/components/date-time-range-fields/date-time-range-fields.component';
import { IDateTimeRangeForm } from '@shared/components/date-time-range-fields/date-time-range-fields.interface';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { QuillModule } from 'ngx-quill';
import { filter } from 'rxjs';
import { IAdminNewsDialogData, INewsForm } from './admin-news-dialog.interface';
import { buildDefaultNewsFormValues, requiredQuillText } from './admin-news-dialog.util';

const MIN_PRIORITY = 0;
const MAX_PRIORITY = 255;

@Component({
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    TranslateModule,
    QuillModule,
    MatErrorComponent,
    DotsSpinnerComponent,
    SideDialogComponent,
    DateTimeRangeFieldsComponent,
  ],
  selector: 'app-admin-news-dialog',
  styleUrl: './admin-news-dialog.component.scss',
  templateUrl: './admin-news-dialog.component.html',
})
export class AdminNewsDialogComponent {
  public readonly ELanguage = ELanguage;
  public readonly languageOptions = Object.values(ELanguage);

  public readonly data = inject<IAdminNewsDialogData>(MAT_DIALOG_DATA);

  // Declared early (ahead of defaultFormValues/form below) - TS class fields initialize in
  // declaration order, and those need this.dateUtils already set.
  private readonly dateUtils = inject(DateUtilitiesService);

  public readonly isCreateMode = this.data.mode === 'create';

  private readonly defaultFormValues = buildDefaultNewsFormValues(this.data.defaultLanguageCode);

  public readonly form = new FormGroup<INewsForm>({
    dates: new FormGroup<IDateTimeRangeForm>({
      endDate: new FormControl<Date | null>(
        this.data.news?.end ? this.dateUtils.parseDateTimeFromApi(this.data.news.end) : this.defaultFormValues.endDate,
        Validators.required,
      ),
      endTime: new FormControl<Date | null>(
        this.data.news?.end ? this.dateUtils.parseDateTimeFromApi(this.data.news.end) : this.defaultFormValues.endTime,
        Validators.required,
      ),
      startDate: new FormControl<Date | null>(
        this.data.news?.start
          ? this.dateUtils.parseDateTimeFromApi(this.data.news.start)
          : this.defaultFormValues.startDate,
        Validators.required,
      ),
      startTime: new FormControl<Date | null>(
        this.data.news?.start
          ? this.dateUtils.parseDateTimeFromApi(this.data.news.start)
          : this.defaultFormValues.startTime,
        Validators.required,
      ),
    }),
    languageCode: new FormControl<ELanguage | null>(
      (this.data.news?.languageCode as ELanguage) ?? this.defaultFormValues.languageCode,
      Validators.required,
    ),
    message: new FormControl<string | null>(this.data.news?.message ?? null, requiredQuillText),
    priority: new FormControl<number | null>(this.data.news?.priority ?? this.defaultFormValues.priority, [
      Validators.required,
      Validators.min(MIN_PRIORITY),
      Validators.max(MAX_PRIORITY),
    ]),
    title: new FormControl<string | null>(this.data.news?.title ?? null, Validators.required),
  });

  public readonly submitLoading = signal<boolean>(false);

  public readonly title = computed(() =>
    this.translate.instant(
      this.isCreateMode ? 'pages.admin.news.dialog.titleCreate' : 'pages.admin.news.dialog.titleEdit',
    ),
  );

  private readonly dialogRef = inject(MatDialogRef<AdminNewsDialogComponent, INews | undefined>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly newsService = inject(NewsService);

  public onSaveClick(): void {
    if (this.form.invalid || this.submitLoading()) {
      this.form.markAllAsTouched();

      return;
    }

    this.submitLoading.set(true);

    const payload = this.buildPayload();
    const request$ =
      this.isCreateMode || !this.data.news?.id
        ? this.newsService.createNews(payload)
        : this.newsService.updateNews(this.data.news.id, payload);

    // No explicit error snackbar here - the global ApiInterceptor already shows one for the failed
    // request.
    request$.subscribe({
      error: () => {
        this.submitLoading.set(false);
      },
      next: (news) => {
        this.submitLoading.set(false);
        this.dialogRef.close(news);
      },
    });
  }

  public onCloseClick(): void {
    if (this.submitLoading()) {
      return;
    }

    if (!this.form.dirty) {
      this.dialogRef.close(undefined);

      return;
    }

    const confirmRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.cancel'),
        confirmText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.confirm'),
        message: this.translate.instant('pages.settings.userManagement.discardChangesDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.settings.userManagement.discardChangesDialog.title'),
      } as IConfirmDialogData,
      width: '400px',
    });

    confirmRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.dialogRef.close(undefined));
  }

  private buildPayload(): INews {
    const { value } = this.form;
    const dates = value.dates!;
    const start = this.dateUtils.formatToApiDateTime(this.dateUtils.combineDateTime(dates.startDate!, dates.startTime));
    const end = this.dateUtils.formatToApiDateTime(this.dateUtils.combineDateTime(dates.endDate!, dates.endTime));

    return {
      end: end ?? undefined,
      id: this.data.news?.id,
      languageCode: value.languageCode!,
      message: value.message!,
      priority: value.priority!,
      start: start ?? undefined,
      title: value.title!,
    };
  }
}
